import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../api'
import { atLeast, type Role } from '../types'
import { Button, Card, CardTitle, ConfirmButton, Field, Input, Note, ErrorText, Badge } from '../components/ui'
import { Select } from '../components/select'

export type TeamMember = { id: string; email: string; name?: string; role: Role; created_at: string }
type Invitation = { id: string; email: string; role: Role; expires_at: string; accepted_at?: string; revoked_at?: string; delivery_status: string }
const choices = [
  { value: 'viewer', label: 'Viewer — view and download' },
  { value: 'member', label: 'Member — create content and spend credits' },
  { value: 'admin', label: 'Admin — manage members and provider keys' },
]

export function TeamSettings({ ws, role }: { ws: string; role?: Role }) {
  const qc = useQueryClient()
  const base = `/api/workspaces/${ws}`
  const canAdmin = atLeast(role, 'admin')
  const canOwn = role === 'owner'
  const roles = choices.filter(item => canOwn || item.value !== 'admin')
  const members = useQuery({ queryKey: ['members', ws], queryFn: () => api.get<TeamMember[]>(`${base}/members`), refetchInterval: 10000 })
  const invitations = useQuery({ queryKey: ['invitations', ws], queryFn: () => api.get<Invitation[]>(`${base}/invitations`), enabled: canAdmin, refetchInterval: 10000 })
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.get<{ user: { id: string } }>('/api/auth/me') })
  const [email, setEmail] = useState('')
  const [inviteRole, setInviteRole] = useState('member')
  const [success, setSuccess] = useState('')
  const [transferUser, setTransferUser] = useState('')
  const [password, setPassword] = useState('')
  const mutation = useMutation({
    mutationFn: (action: () => Promise<unknown>) => action(),
    onMutate: () => setSuccess(''),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['members', ws] })
      qc.invalidateQueries({ queryKey: ['invitations', ws] })
      qc.invalidateQueries({ queryKey: ['me'] })
      qc.invalidateQueries({ queryKey: ['role', ws] })
      setSuccess('Saved.'); setPassword('')
    },
  })
  const canManage = (member: TeamMember) => member.id !== me.data?.user.id && member.role !== 'owner' && (canOwn || (canAdmin && member.role !== 'admin'))
  return <div className="space-y-5">
    <Card>
      <CardTitle>Team members</CardTitle>
      <p className="mt-2 text-sm text-ink-faint">Access applies to this workspace. Members and above can spend its provider credits.</p>
      {(members.error || mutation.error) && <ErrorText error={members.error || mutation.error} />}
      {success && <Note>{success}</Note>}
      {members.isPending && <p>Loading members…</p>}
      <div className="mt-4 space-y-4">
        {members.data?.map(member => <div key={member.id} className="flex flex-wrap items-center justify-between gap-3 border-b border-line py-3">
          <div><p>{member.name || member.email}</p><p className="text-sm text-ink-faint">{member.email} · Joined {new Date(member.created_at).toLocaleDateString()}</p></div>
          <div className="flex items-center gap-3">
            {canManage(member) ? <Select ariaLabel={`Role for ${member.email}`} value={member.role} items={roles} onValueChange={value => mutation.mutate(() => api.patch(`${base}/members/${member.id}`, { role: value }))} /> : <Badge>{member.role}</Badge>}
            {canManage(member) && <ConfirmButton disabled={mutation.isPending} confirmLabel={`Remove ${member.email}?`} onConfirm={() => mutation.mutate(() => api.del(`${base}/members/${member.id}`))}>Remove</ConfirmButton>}
            {member.id === me.data?.user.id && (member.role !== 'owner' || (members.data?.filter(item => item.role === 'owner').length ?? 0) > 1) && <ConfirmButton disabled={mutation.isPending} confirmLabel="Leave this workspace?" onConfirm={() => mutation.mutate(() => api.post(`${base}/leave`))}>Leave</ConfirmButton>}
          </div>
        </div>)}
      </div>
    </Card>
    {canAdmin && <Card>
      <CardTitle>Invite a teammate</CardTitle>
      <form className="mt-4 space-y-4" onSubmit={event => { event.preventDefault(); mutation.mutate(async () => { await api.post(`${base}/invitations`, { email, role: inviteRole }); setEmail('') }) }}>
        <Field label="Email"><Input type="email" required value={email} onChange={event => setEmail(event.target.value)} /></Field>
        <Field label="Role"><Select value={inviteRole} items={roles} onValueChange={setInviteRole} /></Field>
        <Note>They receive an email and choose their own password. Invitations expire after 7 days.</Note>
        <Button type="submit" variant="primary" disabled={mutation.isPending || !email}>Send invitation</Button>
      </form>
    </Card>}
    {canAdmin && <Card>
      <CardTitle>Invitations</CardTitle>
      {invitations.error && <ErrorText error={invitations.error} />}
      {invitations.data?.length === 0 && <p className="mt-3 text-sm text-ink-faint">No invitations yet.</p>}
      {invitations.data?.map(invite => {
        const expired = new Date(invite.expires_at).getTime() <= Date.now()
        const active = !invite.accepted_at && !invite.revoked_at
        const status = invite.accepted_at ? 'Accepted' : invite.revoked_at ? 'Revoked' : expired ? 'Expired' : 'Pending'
        const manageable = canOwn || invite.role !== 'admin'
        return <div key={invite.id} className="flex flex-wrap items-center justify-between gap-3 border-b border-line py-3">
          <div><p>{invite.email} · {invite.role}</p><p className="text-sm text-ink-faint">{status} · Email: {invite.delivery_status === 'sent' ? 'Submitted to Postmark' : invite.delivery_status} · Expires {new Date(invite.expires_at).toLocaleString()}</p></div>
          {active && manageable && <div className="flex gap-2">
            <Button disabled={mutation.isPending} onClick={() => mutation.mutate(() => api.post(`${base}/invitations/${invite.id}/resend`))}>Resend</Button>
            <ConfirmButton disabled={mutation.isPending} confirmLabel="Revoke this invitation?" onConfirm={() => mutation.mutate(() => api.del(`${base}/invitations/${invite.id}`))}>Revoke</ConfirmButton>
          </div>}
        </div>
      })}
    </Card>}
    {canOwn && <Card>
      <CardTitle>Transfer workspace ownership</CardTitle>
      <form className="mt-4 space-y-4" onSubmit={event => event.preventDefault()}>
        <Note>The selected teammate becomes an owner and your role becomes admin. Organization ownership stays unchanged.</Note>
        <Field label="New owner"><Select value={transferUser} items={(members.data ?? []).filter(member => member.id !== me.data?.user.id && member.role !== 'owner').map(member => ({ value: member.id, label: member.email }))} onValueChange={setTransferUser} placeholder="Choose a teammate" /></Field>
        <Field label="Your current password"><Input type="password" autoComplete="current-password" value={password} onChange={event => setPassword(event.target.value)} /></Field>
        <ConfirmButton disabled={mutation.isPending || !transferUser || !password} confirmLabel="Transfer ownership?" onConfirm={() => mutation.mutate(() => api.post(`${base}/ownership`, { user_id: transferUser, password }))}>Transfer ownership</ConfirmButton>
      </form>
    </Card>}
  </div>
}
